import { themeZafire } from '@zafire/ag-grid-enterprise';
import { ZfTypography } from '@zafire/react';
import type { Theme } from 'ag-grid-community';
import type { AgGridReactProps } from 'ag-grid-react';
import { AgGridReact } from 'ag-grid-react';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLayout } from '../../../contexts/layout-context';
import { useWindowResize } from '../../../hooks/useWindowResize';
import { AgGridTableContainer } from './AgGridTableContainer';

const DEFAULT_SCREEN_HEADER_PADDINGS_AND_HEIGHT = 0;
const DEFAULT_HEADER_ROW_HEIGHT = 45;
const DEFAULT_TABLE_ROW_HEIGHT = 56;
const DEFAULT_TABLE_GROUP_ROW_HEIGHT = 35;
const DEFAULT_TABLE_CONTAINER_PADDING = 16;

const MAGIC_TABLE_NUMBER = 16;
const MAGIC_TABLE_ROW_GROUPS_NUMBER = 1;

interface IAgGridTableProps<TData> extends AgGridReactProps<TData> {
    /** Wrap the grid in {@link AgGridTableContainer} and size height from row counts (otherwise height follows parent). */
    addTableContainer?: boolean;
    /** Header row height (px). Default 45. */
    headerRowHeight?: number;
    /** Body row height (px). Default 56. */
    tableRowHeight?: number;
    /** Group row height when using row grouping (px). Default 35. */
    tableGroupRowHeight?: number;
    /** Detail row height for master/detail (px); omit if unused. */
    tableDetailRowHeight?: number;
    /** Horizontal cell padding passed into the theme (px). */
    cellHorizontalPadding?: number;
    /** Container padding on each side (px); doubled in height math. Default 16. */
    tableContainerPadding?: number;
    /** Extra vertical space above the grid subtracted from viewport when computing max height (headers, margins). */
    screenHeaderPaddingsAndHeight?: number;
    /** Size the grid to fit all rows, ignoring the viewport max-height cap (grid never scrolls; let an ancestor scroll instead). */
    fitContentHeight?: boolean;
    /** Extra px on auto height ({@link addTableContainer}); avoids stray scrollbar from borders/rounding, esp. with padding 0. */
    tableHeightSlackPx?: number;
    /** Empty-state overlay copy; falls back to i18n `emptyData`. */
    emptyTablePlaceholderText?: string;
    /** AG Grid theme; defaults to Zafire minimal and comfort density. */
    theme?: Theme;
}

export const AgGridTable = <TData,>({
    rowData,
    addTableContainer,
    headerRowHeight,
    tableRowHeight,
    tableGroupRowHeight,
    tableDetailRowHeight,
    cellHorizontalPadding,
    tableContainerPadding,
    screenHeaderPaddingsAndHeight,
    fitContentHeight = false,
    tableHeightSlackPx = 0,
    emptyTablePlaceholderText,
    theme,
    ...agGridProps
}: IAgGridTableProps<TData>) => {
    const { t } = useTranslation();
    const { totalNavbarHeight, bodyElement } = useLayout();

    const gridRef = useRef<AgGridReact>(null);
    const [tableContainerHeight, setTableContainerHeight] = useState(0);

    const [headerHeight, rowHeight, groupRowHeight] = useMemo(
        () => [headerRowHeight ?? DEFAULT_HEADER_ROW_HEIGHT, tableRowHeight ?? DEFAULT_TABLE_ROW_HEIGHT, tableGroupRowHeight ?? DEFAULT_TABLE_GROUP_ROW_HEIGHT],
        [headerRowHeight, tableRowHeight, tableGroupRowHeight],
    );

    const computeVisibleRowCount = useCallback(() => {
        const api = gridRef.current?.api;
        if (!api) return { visibleRows: rowData?.length ?? 0, visibleGroupRows: 0, visibleDetailRows: 0 };

        let visibleRows = 0;
        let visibleGroupRows = 0;
        let visibleDetailRows = 0;

        api.forEachNodeAfterFilterAndSort((node) => {
            if (node.displayed) {
                if (node.group) visibleGroupRows += 1;
                else {
                    if (node.master && node.expanded && tableDetailRowHeight) visibleDetailRows += 1;
                    visibleRows += 1;
                }
            }
        });

        return { visibleRows, visibleGroupRows, visibleDetailRows };
    }, [tableDetailRowHeight, rowData]);

    const calculateTableHeight = useCallback(() => {
        const { visibleRows, visibleGroupRows, visibleDetailRows } = computeVisibleRowCount();
        const groupRowsHeight = visibleGroupRows ? visibleGroupRows * groupRowHeight + MAGIC_TABLE_ROW_GROUPS_NUMBER : 0;
        const detailRowsHeight = visibleDetailRows && tableDetailRowHeight ? visibleDetailRows * tableDetailRowHeight : 0;
        const containerPadding = (tableContainerPadding ?? DEFAULT_TABLE_CONTAINER_PADDING) * 2;
        const screenPaddingsAndHeight = screenHeaderPaddingsAndHeight ?? DEFAULT_SCREEN_HEADER_PADDINGS_AND_HEIGHT;

        const tableHeight =
            visibleRows * rowHeight + headerHeight + groupRowsHeight + detailRowsHeight + containerPadding + tableHeightSlackPx + MAGIC_TABLE_NUMBER;

        if (fitContentHeight) {
            setTableContainerHeight(tableHeight);

            return;
        }

        const maxHeight = bodyElement?.clientHeight
            ? bodyElement.clientHeight - screenPaddingsAndHeight
            : window.innerHeight - totalNavbarHeight - screenPaddingsAndHeight;

        setTableContainerHeight(Math.min(tableHeight, maxHeight));
    }, [
        computeVisibleRowCount,
        headerHeight,
        rowHeight,
        groupRowHeight,
        tableDetailRowHeight,
        tableContainerPadding,
        screenHeaderPaddingsAndHeight,
        fitContentHeight,
        tableHeightSlackPx,
        totalNavbarHeight,
        bodyElement,
    ]);

    useEffect(() => {
        if (rowData?.length) calculateTableHeight();
    }, [rowData, calculateTableHeight]);

    useLayoutEffect(() => {
        if (rowData?.length) calculateTableHeight();
    }, [rowData, calculateTableHeight]);

    useWindowResize(calculateTableHeight);

    const handleRowGroupOpened = useCallback(() => calculateTableHeight(), [calculateTableHeight]);

    return (
        <AgGridTableContainer show={addTableContainer} height={tableContainerHeight} padding={tableContainerPadding ?? DEFAULT_TABLE_CONTAINER_PADDING}>
            <AgGridReact
                ref={gridRef}
                theme={(theme ?? themeZafire.withZafire('minimal').withZafire('density', 'comfort')).withParams({
                    rowHeight,
                    headerHeight,
                    cellHorizontalPadding,
                })}
                rowData={rowData}
                domLayout="normal"
                enableCellTextSelection
                ensureDomOrder
                getRowHeight={({ node }) => (node.group ? groupRowHeight : rowHeight)}
                onRowGroupOpened={handleRowGroupOpened}
                onViewportChanged={calculateTableHeight}
                noRowsOverlayComponent={() => (
                    <ZfTypography variant="body-extra-large" color="disabled">
                        {emptyTablePlaceholderText || t('emptyData')}
                    </ZfTypography>
                )}
                {...agGridProps}
            />
        </AgGridTableContainer>
    );
};
